import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import InlineTextInput from '@/components/ui/inline-text-input';
import { Toggle } from '@/components/ui/toggle';
import { centsToInitialValue, currencyInputValueToCents } from '@/lib/currency';
import { cn } from '@/lib/utils';
import type { ResellType } from '@/models/types';
import { isNumber } from 'lodash';
import { useState } from 'react';
// import { ResellTypeSelectorCombobox } from './inputs/resell-type-selector-combobox';

// type Condition = "excellent" | "good" | "fair" | "poor" | "none" | string;

// paid_off: boolean;
//   lienholder: string;
//   state: string;
//   postal_code: string;
//   number_of_keys: number;
//   asking_price: number;
//   exterior_color: string;
//   interior_color: string;
//   offer: number;

const Row = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-row justify-between items-center w-full">
      {children}
    </div>
  );
};

const TradeInDialog = ({
  initialVin,
  intialMake,
  initialModel,
  initialTrim,
  initialYear,
  initialMileage,
  initialCondition,
  initialPaidOff,
  initialLienholder,
  initialState,
  initialPostalCode,
  initialNumberOfKeys,
  initialAskingPrice,
  initialExteriorColor,
  initialInteriorColor,
  initialOffer,
  initialResellType,
  onClickCta,
  ctaText,
  onOpen,
  onClose,
  dialogIsOpen,
  children,
}: {
  initialVin: string;
  intialMake: string;
  initialModel: string;
  initialTrim: string;
  initialYear: string;
  initialMileage: string;
  initialCondition: string;
  initialPaidOff: boolean;
  initialLienholder: string;
  initialState: string;
  initialPostalCode: string;
  initialNumberOfKeys: number;
  initialAskingPrice: number;
  initialExteriorColor: string;
  initialInteriorColor: string;
  initialOffer: number;
  initialResellType: ResellType;
  onClickCta: ({
    vinValue,
    makeValue,
    modelValue,
    trimValue,
    yearValue,
    mileageValue,
    conditionValue,
    paidOffValue,
    lienholderValue,
    stateValue,
    postalCodeValue,
    numberOfKeysValue,
    askingPriceValue,
    exteriorColorValue,
    interiorColorValue,
    offerValue,
    resellTypeValue,
  }: {
    vinValue: string;
    makeValue: string;
    modelValue: string;
    trimValue: string;
    yearValue: string;
    mileageValue: string;
    conditionValue: string;
    paidOffValue: boolean;
    lienholderValue: string;
    stateValue: string;
    postalCodeValue: string;
    numberOfKeysValue: number;
    askingPriceValue: number;
    exteriorColorValue: string;
    interiorColorValue: string;
    offerValue: number;
    resellTypeValue: ResellType;
  }) => void;
  ctaText: string;
  onOpen: () => void;
  onClose: () => void;
  dialogIsOpen: boolean;
  children?: React.ReactNode;
}) => {
  const [vinValue, setVinValue] = useState(initialVin);
  const [makeValue, setMakeValue] = useState(intialMake);
  const [modelValue, setModelValue] = useState(initialModel);
  const [trimValue, setTrimValue] = useState(initialTrim);
  const [yearValue, setYearValue] = useState(initialYear);
  const [mileageValue, setMileageValue] = useState(initialMileage);
  const [conditionValue, setConditionValue] = useState(initialCondition);
  const [paidOffValue, setPaidOffValue] = useState(initialPaidOff);
  const [lienholderValue, setLienholderValue] = useState(initialLienholder);
  const [stateValue, setStateValue] = useState(initialState);
  const [postalCodeValue, setPostalCodeValue] = useState(initialPostalCode);
  const [numberOfKeysValue, setNumberOfKeysValue] =
    useState(initialNumberOfKeys);
  const [askingPriceValue, setAskingPriceValue] = useState(initialAskingPrice);
  const [exteriorColorValue, setExteriorColorValue] =
    useState(initialExteriorColor);
  const [interiorColorValue, setInteriorColorValue] =
    useState(initialInteriorColor);
  const [offerValue, setOfferValue] = useState(initialOffer);
  const [resellTypeValue, _] = useState(initialResellType);

  return (
    <Dialog open={dialogIsOpen}>
      <DialogTrigger asChild onClick={onOpen}>
        {children}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto"
        onClickClose={onClose}
        onPointerDownOutside={onClose}
      >
        <DialogHeader>
          <DialogTitle>Trade-In</DialogTitle>
          <DialogDescription>A trade-in to the deal.</DialogDescription>
        </DialogHeader>
        <Row>
          <label className="text-sm">VIN</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add VIN"
            value={vinValue}
            onChange={(e) => setVinValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Make</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Make"
            value={makeValue}
            onChange={(e) => setMakeValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Model</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Model"
            value={modelValue}
            onChange={(e) => setModelValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Trim</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Trim"
            value={trimValue}
            onChange={(e) => setTrimValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Year</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Year"
            value={yearValue}
            onChange={(e) => setYearValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Mileage</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Mileage"
            value={mileageValue}
            onChange={(e) => setMileageValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Paid Off Status</label>
          <Toggle
            className="text-gray-500 rounded text-xs w-fit p-0 m-0"
            pressed={paidOffValue}
            onPressedChange={(pressed: boolean) => setPaidOffValue(pressed)}
          >
            Paid Off: {paidOffValue ? 'Yes' : 'No'}
          </Toggle>
        </Row>
        <Row>
          <label className="text-sm">Lienholder</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Lienholder"
            value={lienholderValue}
            onChange={(e) => setLienholderValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">State</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add State"
            value={stateValue}
            onChange={(e) => setStateValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Postal Code</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Postal Code"
            value={postalCodeValue}
            onChange={(e) => setPostalCodeValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Condition</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Condition"
            value={conditionValue}
            onChange={(e) => setConditionValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Number of Keys</label>
          <InlineTextInput
            wrapperClassName={cn(
              'w-full',
              isNumber(numberOfKeysValue) && 'w-12',
            )}
            className="rounded text-xs w-full"
            placeholder="Add Number of Keys"
            value={numberOfKeysValue?.toString()}
            onChange={(e) => {
              if (isNaN(Number(e.target.value))) return;
              setNumberOfKeysValue(Number(e.target.value));
            }}
            endAdornment={
              isNumber(numberOfKeysValue) ? (
                <div className="text-gray-500 text-xs"> keys</div>
              ) : null
            }
          />
        </Row>
        <Row>
          <label className="text-sm">Asking Price</label>
          <CurrencyInput
            value={centsToInitialValue(askingPriceValue)}
            onChange={(e) => {
              const rawValue = e.target.value;

              setAskingPriceValue(currencyInputValueToCents(rawValue));
            }}
            placeholder="Add Asking Price"
            className={cn('text-right w-20')}
          />
        </Row>
        <Row>
          <label className="text-sm">Exterior Color</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Exterior Color"
            value={exteriorColorValue}
            onChange={(e) => setExteriorColorValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Interior Color</label>
          <InlineTextInput
            className="rounded text-xs text-right"
            placeholder="Add Interior Color"
            value={interiorColorValue}
            onChange={(e) => setInteriorColorValue(e.target.value)}
          />
        </Row>
        <Row>
          <label className="text-sm">Offer</label>
          <CurrencyInput
            value={centsToInitialValue(offerValue)}
            onChange={(e) => {
              const rawValue = e.target.value;

              setOfferValue(currencyInputValueToCents(rawValue));
            }}
            placeholder="Add Offer"
            className={cn('text-right w-20')}
          />
        </Row>

        <DialogFooter>
          <Button
            type="submit"
            onClick={() =>
              onClickCta({
                vinValue,
                makeValue,
                modelValue,
                trimValue,
                yearValue,
                mileageValue,
                conditionValue,
                paidOffValue,
                lienholderValue,
                stateValue,
                postalCodeValue,
                numberOfKeysValue,
                askingPriceValue,
                exteriorColorValue,
                interiorColorValue,
                offerValue,
                resellTypeValue,
              })
            }
          >
            {ctaText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TradeInDialog;
